/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { faFileAlt, faInfoCircle, faPlus, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Col, Collapse, Container, OverlayTrigger, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  findSectionByCodeAndSurvey,
  getSectionNameByCodeAndSurvey,
} from '../../actions/helpers/section.helper';
import benTooltip from '../../Components/BenTooltip';
import { ONBOARDING_STEPS } from '../../constants/common';
import types from '../../types';

import OtherFilters from './components/OtherFilters';
import QuestionEditForm from './components/QuestionEditForm';
import SampleDimension404Dialog from './components/QuestionEditForm/sampleDimension404Dialog';
import SectionEditForm from './components/SectionEditForm';
import SectionQuestions from './components/SectionQuestions';
import SurveyInformationCard from './components/SurveyInformationCard';
import SurveyInformationEditForm from './components/SurveyInformationEditForm';
import useOnboarding from './hooks/useOnboarding';
import useQuestionEditor from './hooks/useQuestionEditor';
import useSectionQuestions from './hooks/useSectionQuestions';
import useSurveyData from './hooks/useSurveyData';
import useWizardNav, { WIZARD_AREAS } from './hooks/useWizardNav';
import saveSurveySection from './utils/saveSurveySection';
import './styles.css';

// The backend clears query caches asynchronously after a save; give it a
// moment before refetching the preview data (same grace the old wizard used).
const PREVIEW_REFRESH_DELAY_MS = 2000;

const SurveyEditWizard = () => {
  const dispatch = useDispatch();
  const { id: surveyId } = useParams();

  const nav = useWizardNav();
  const data = useSurveyData(surveyId);
  const sectionQuestions = useSectionQuestions(surveyId, data.jwt);
  const editor = useQuestionEditor(data.jwt);
  const { isStepVisible, completeStep } = useOnboarding({ load: true });
  const [questionRefreshToken, setQuestionRefreshToken] = useState(0);
  // True from the moment a question save lands until the preview has been
  // refetched from the backend, so the loading overlay shows continuously
  // instead of flashing once for the save and again for the reload.
  const [previewRefreshing, setPreviewRefreshing] = useState(false);
  // Saving flags for the two sidebar forms, so each can block its own inputs
  // while its request is in flight.
  const [savingSurveyInfo, setSavingSurveyInfo] = useState(false);
  const [savingSection, setSavingSection] = useState(false);

  // Signals from shared components that stay redux-connected:
  // MoveQuestionToSection announces "move to section" / reorder through these.
  const { section: sectionToMove, questionId: sampleToMoveId } = useSelector(
    (state) => state.sections
  );
  const { sampleMoved, loading: sampleActionLoading } = useSelector((state) => state.surveySample);
  const { openedQuestions } = useSelector((state) => state.surveyDetails);
  const openedQuestionsRef = useRef(openedQuestions);
  openedQuestionsRef.current = openedQuestions;
  // Which question panel to reopen once a save has finished refreshing.
  const reopenQuestionIdRef = useRef(null);

  const { currentSurvey, allSections } = data;

  const handleSelectSection = useCallback(
    (sectionCode, sectionName) => {
      const sectionData = findSectionByCodeAndSurvey(sectionCode, currentSurvey, allSections);
      if (sectionData) {
        // Keep the survey's bare section code on the selection: that is what
        // the questions endpoint and the name lookup expect, while the entry's
        // own code may already be the composite form.
        nav.selectSection({
          ...sectionData,
          Data: { ...sectionData.Data, code: sectionCode },
        });
      } else {
        if (sectionCode === '') {
          nav.openEditor(WIZARD_AREAS.SECTIONS);
          sectionQuestions.clear();
        }
        nav.selectSection({
          Id: -1,
          Data: {
            code: sectionCode,
            name: sectionName,
          },
        });
      }
    },
    [allSections, currentSurvey, nav, sectionQuestions]
  );

  // Selected section changed -> fetch its questions.
  useEffect(() => {
    if (
      nav.selectedSection &&
      (nav.selectedSection.Id !== -1 || nav.selectedSection.Data.code !== '')
    ) {
      sectionQuestions.load(nav.selectedSection.Data.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav.selectedSection]);

  // "Move to another section" requested from the shared question dropdown.
  useEffect(() => {
    if (!sectionToMove || !sampleToMoveId) return;
    const identifierParts = sectionToMove.identifier.split('_');
    identifierParts[2] = sectionToMove.sectionCode.toUpperCase();
    const identifier = identifierParts.join('_');

    (async () => {
      try {
        await editor.moveQuestionToSection({
          questionId: sampleToMoveId,
          sectionCode: sectionToMove.sectionCode,
          identifier,
          surveyId: sectionToMove.surveyId,
        });
        nav.selectArea(WIZARD_AREAS.SECTIONS);
        handleSelectSection(sectionToMove.sectionCode.trim(), sectionToMove.sectionName.trim());
      } catch (e) {
        console.error(e);
      } finally {
        dispatch({ type: types.reducerTypes.UPDATED_SECTION_ON_QUESTION });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionToMove, sampleToMoveId]);

  // Reordering questions (shared dropdown) went through redux -> refresh list.
  useEffect(() => {
    if (!sampleMoved) return;
    sectionQuestions.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleMoved]);

  // The question sample/dimensions could not be loaded -> tell the user and
  // drop back to the section overview.
  useEffect(() => {
    if (!editor.loadError) return;
    nav.selectArea(WIZARD_AREAS.SECTIONS);
    SampleDimension404Dialog(dispatch);
    editor.clearLoadError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.loadError]);

  // A save is starting: collapse the expanded question panel so its chart is
  // torn down instead of lingering on the pre-save render, remembering which
  // one to reopen when the refreshed data is ready.
  const handleQuestionSave = useCallback(
    (payload) => {
      reopenQuestionIdRef.current = openedQuestionsRef.current?.[0] ?? null;
      dispatch({ type: types.reducerTypes.SURVEY_DETAILS_CLEAR_ALL_OPEN_QUESTIONS });
      return editor.saveQuestion(payload);
    },
    [dispatch, editor]
  );

  // The save landed: keep the loading state up, wait out the backend cache
  // rebuild, refetch the preview data, then reopen the question panel and
  // reload it. Loading only clears once that reload reports back
  // (handleQuestionRefreshed), so it stays continuous from save to fresh data.
  const handleQuestionSaved = useCallback(() => {
    setPreviewRefreshing(true);
    setTimeout(async () => {
      // Refetch the visualizations AND the section's questions: the question
      // list carries the chart data each preview falls back to, so without it
      // the chart keeps rendering the pre-save result set.
      await Promise.all([data.refreshPreviewData(), sectionQuestions.reload({ silent: true })]);
      const reopenId = reopenQuestionIdRef.current;
      if (reopenId) {
        // Reopen the panel BEFORE asking it to reload: the reload effect only
        // runs for an expanded question, and clearing the loading state
        // depends on that reload reporting back.
        dispatch({
          type: types.reducerTypes.SURVEY_DETAILS_ONLY_OPEN_QUESTIONS,
          payload: { questionId: reopenId },
        });
        reopenQuestionIdRef.current = null;
        setQuestionRefreshToken((token) => token + 1);
      } else {
        // No expanded question to reload -> nothing further to wait for.
        setPreviewRefreshing(false);
      }
    }, PREVIEW_REFRESH_DELAY_MS);
  }, [data, dispatch, sectionQuestions]);

  const handleQuestionRefreshed = useCallback(() => {
    setPreviewRefreshing(false);
  }, []);

  const handleSurveyInfoSave = useCallback(
    async (surveyInfo) => {
      setSavingSurveyInfo(true);
      try {
        await data.saveSurveyInfo(surveyInfo);
      } catch (e) {
        console.error(e);
      } finally {
        setSavingSurveyInfo(false);
      }
    },
    [data]
  );

  const handleSectionSave = useCallback(
    async (sectionInfo) => {
      setSavingSection(true);
      try {
        const updatedSection = await saveSurveySection({
          sectionInfo,
          selectedSection: nav.selectedSection,
          survey: currentSurvey,
          jwt: data.jwt,
        });
        nav.selectSection(updatedSection);
        await data.refetchSections();
      } catch (e) {
        console.error(e);
      } finally {
        setSavingSection(false);
      }
    },
    [nav, currentSurvey, data]
  );

  const sortSections = (a, b) => {
    if (a === b) return 0;
    return a < b ? -1 : 1;
  };

  const showSectionDescriptions = () => {
    return currentSurvey.surveySummaryDetailsDto?.sectionCodes
      .split(',')
      .sort(sortSections)
      .map((code) => {
        const selectedSectionCode = code.trim().toLowerCase();
        const sectionName = getSectionNameByCodeAndSurvey(
          selectedSectionCode,
          currentSurvey,
          allSections
        );
        const alertVariant =
          nav.selectedSection?.Data?.code !== selectedSectionCode
            ? 'alert-border'
            : 'alert-border-selected';
        return (
          <Alert
            key={code.trim()}
            variant="light"
            className={`alert-container ${alertVariant}`}
            onClick={() => handleSelectSection(code.trim(), sectionName.trim())}
          >
            {sectionName.trim()}
          </Alert>
        );
      });
  };

  const showStep1 = isStepVisible(ONBOARDING_STEPS.step1);
  const sectionAreaActive = [WIZARD_AREAS.SECTIONS, WIZARD_AREAS.QUESTION].includes(nav.area);
  const previewBusy = editor.loading || editor.saving || sampleActionLoading || previewRefreshing;
  // The single loading condition both panels use, so the sidebar form and the
  // preview always start and finish loading together. It covers the question
  // save/refresh cycle, a section save (which re-selects and reloads the
  // section), and the section question list loading on its own.
  const rightPanelBusy = previewBusy || sectionQuestions.isLoading || savingSection || savingSurveyInfo;

  if (data.loading) {
    return (
      <Container fluid>
        <Row>
          <Col xs="12" className="d-flex justify-content-center mt-5">
            <Spinner animation="border" />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="survey-wizard-container">
      <Row>
        {!nav.editing && (
          <Col md="3" className="pt-5 survey-sidebar p-0 pr-5">
            <div className="pt-3 pb-4 pl-5">
              <p>Survey</p>
              <h2>{currentSurvey?.title}</h2>
            </div>
            <Alert
              variant="light"
              className={`alert-container alert-border ${
                nav.area === WIZARD_AREAS.INFO ? 'section-selected' : ''
              }`}
              onClick={() => nav.selectArea(WIZARD_AREAS.INFO)}
            >
              <FontAwesomeIcon size="lg" className="mr-3" icon={faInfoCircle} />
              Survey information
            </Alert>
            <OverlayTrigger
              placement="right"
              defaultShow={showStep1}
              show={showStep1}
              overlay={(props) =>
                benTooltip({
                  ...props,
                  title: ONBOARDING_STEPS.step1.title,
                  content: ONBOARDING_STEPS.step1.content,
                  close: () => completeStep(ONBOARDING_STEPS.step1.value),
                })
              }
            >
              <Alert
                variant="light"
                className={`alert-container alert-border ${
                  sectionAreaActive ? 'section-selected' : ''
                }`}
                onClick={() => nav.selectArea(WIZARD_AREAS.SECTIONS)}
              >
                <FontAwesomeIcon size="lg" className="mr-3" icon={faFileAlt} />
                Sections
              </Alert>
            </OverlayTrigger>
            {nav.area === WIZARD_AREAS.SECTIONS && (
              <Collapse in={nav.area === WIZARD_AREAS.SECTIONS}>
                <Container className="pl-3 sidebar-section-container">
                  <Alert
                    variant="light"
                    className="alert-container d-none"
                    onClick={() => handleSelectSection('', '')}
                  >
                    <FontAwesomeIcon size="lg" className="mr-3" icon={faPlus} />
                    New Section
                  </Alert>
                  {showSectionDescriptions()}
                </Container>
              </Collapse>
            )}
            <Alert
              variant="light"
              className={`alert-container alert-border d-none ${
                nav.area === WIZARD_AREAS.FILTERS ? 'section-selected' : ''
              }`}
              onClick={() => nav.selectArea(WIZARD_AREAS.FILTERS)}
            >
              <FontAwesomeIcon size="lg" className="mr-3" icon={faSlidersH} />
              Other Filters
            </Alert>
          </Col>
        )}
        {nav.editing && nav.area === WIZARD_AREAS.INFO && (
          <SurveyInformationEditForm
            currentSurvey={currentSurvey}
            metadataDescriptions={data.metadataDescriptions}
            busy={savingSurveyInfo || rightPanelBusy}
            busyLabel="Saving survey information"
            handleEditMode={nav.closeEditor}
            handleSave={handleSurveyInfoSave}
          />
        )}
        {nav.editing && nav.area === WIZARD_AREAS.SECTIONS && (
          <SectionEditForm
            currentCode={nav.selectedSection?.Data.code}
            currentSection={getSectionNameByCodeAndSurvey(
              nav.selectedSection?.Data.code,
              currentSurvey,
              allSections
            )}
            busy={rightPanelBusy}
            busyLabel="Saving section"
            handleEditMode={nav.closeEditor}
            handleSave={handleSectionSave}
          />
        )}
        {nav.editing && nav.area === WIZARD_AREAS.QUESTION && (
          <QuestionEditForm
            sample={editor.sample}
            dimensions={editor.dimensions}
            visualizations={data.visualizations}
            surveyLocationFilter={data.surveyLocationFilter}
            busy={rightPanelBusy}
            busyLabel={
              editor.saving || previewRefreshing ? 'Saving your changes' : 'Loading question'
            }
            onClose={nav.closeEditor}
            onSave={handleQuestionSave}
            onSaved={handleQuestionSaved}
            mirrorToStore={editor.mirrorToStore}
          />
        )}
        <Col
          md="9"
          className={clsx(['survey-sections-container pt-5', { 'empty-section': !nav.area }])}
        >
          {!nav.area && (
            <>
              <img src="/empty-section.svg" alt="select a section" />
              <h2>Choose a Section to edit</h2>
            </>
          )}
          {nav.area === WIZARD_AREAS.INFO && (
            <SurveyInformationCard
              busy={savingSurveyInfo || rightPanelBusy}
              currentSurvey={currentSurvey}
              metadataDescriptions={data.metadataDescriptions}
              onEditModeClick={() => nav.openEditor(WIZARD_AREAS.INFO)}
            />
          )}
          {nav.selectedSection && sectionAreaActive && (
            <SectionQuestions
              sectionName={getSectionNameByCodeAndSurvey(
                nav.selectedSection.Data.code,
                currentSurvey,
                allSections
              )}
              sectionCode={nav.selectedSection.Data.code}
              surveyQuestions={sectionQuestions.surveyQuestions}
              visualizations={data.visualizations}
              busy={rightPanelBusy}
              isEditSection={nav.editing && nav.area === WIZARD_AREAS.SECTIONS}
              refreshToken={questionRefreshToken}
              onEditSectionClick={() => nav.openEditor(WIZARD_AREAS.SECTIONS)}
              onSelectQuestion={() => nav.openEditor(WIZARD_AREAS.QUESTION)}
              onLoadQuestion={editor.loadQuestion}
              onQuestionRefreshed={handleQuestionRefreshed}
            />
          )}
          {nav.area === WIZARD_AREAS.FILTERS && <OtherFilters />}
        </Col>
      </Row>
    </Container>
  );
};

export default SurveyEditWizard;
